import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { graphql } from '@/gql'
import { gqlRequest, graphqlErrorMessage, type RequestSession } from '@/lib/api/client'
import { recipeCostOre } from '@/lib/costing'
import { createRecord } from '@/lib/records'
import { useConfiguration } from '@/lib/useConfiguration'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog'
import { Input } from './ui/input'

const WasteMenuQuery = graphql(`query GalleyWasteMenu($restaurantId: ID!) {
  restaurant(id: $restaurantId) { id canWrite: hasAccess(access: Write)
    menu(filter: { active: All, includeProductsWithOpenPrice: true }) {
      pages { categories { products { id name recipe: attribute(key: "galley.recipe") unitCost: attribute(key: "galley.unitCostOre") } } }
    }
  }
}`)
export function WasteDialog({ open, onClose, session, restaurantId }: { open: boolean; onClose: () => void; session: RequestSession; restaurantId: string }) {
  const { config } = useConfiguration(session, restaurantId)
  const query = useQuery({ queryKey: ['wasteMenu', session.mode, restaurantId], enabled: open, queryFn: () => gqlRequest(session, WasteMenuQuery, { restaurantId }) })
  const [productId, setProductId] = useState('')
  const [search, setSearch] = useState('')
  const [count, setCount] = useState('1')
  const [unitPrice, setUnitPrice] = useState('')
  const [reason, setReason] = useState('overproduced')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const products = [...new Map((query.data?.restaurant?.menu?.pages ?? []).flatMap((p) => p.categories?.flatMap((c) => c.products ?? []) ?? []).map((p) => [p.id, p])).values()]
  const selected = products.find((p) => p.id === productId)
  const recipe = selected ? (config.recipes[selected.id] ?? selected.recipe) : null
  const computedCost = recipeCostOre(recipe, config.ingredients)
  const price = unitPrice ? Math.round(Number(unitPrice.replace(',', '.')) * 100) : (computedCost ?? (typeof selected?.unitCost === 'number' ? selected.unitCost : 0))
  const save = async () => {
    const amount = Number(count.replace(',', '.'))
    if (!selected || !Number.isFinite(amount) || amount <= 0 || !Number.isFinite(price) || price < 0) {
      setError('Välj produkt och ange ett giltigt antal och värde.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await createRecord(session, restaurantId, `galley.waste.${new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm' }).format(new Date())}`, {
        productId,
        name: selected.name,
        amount,
        unit: 'st',
        unitPriceOre: price,
        category: reason,
        note: note.trim() || null,
        recipe: recipe ?? null,
        createdAt: new Date().toISOString(),
      })
      setProductId('')
      setCount('1')
      setUnitPrice('')
      setNote('')
      onClose()
    } catch (cause) {
      setError(graphqlErrorMessage(cause))
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value && !saving) onClose()
      }}
    >
      <DialogContent>
        <DialogTitle>Registrera matsvinn</DialogTitle>
        <DialogDescription>Produkt, antal och anledning sparas i restaurangens svinnhistorik.</DialogDescription>
        <Input aria-label="Sök produkt" placeholder="Sök produkt" value={search} onChange={(e) => setSearch(e.target.value)} />
        <label>
          Produkt
          <select
            className="w-full rounded border bg-card p-3"
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value)
              setUnitPrice('')
            }}
          >
            <option value="">Välj produkt</option>
            {products
              .filter((p) => p.id === productId || p.name?.toLowerCase().includes(search.toLowerCase()))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          Antal
          <Input value={count} inputMode="decimal" onChange={(e) => setCount(e.target.value)} />
        </label>
        <label>
          Värde per portion (kr)
          <Input value={unitPrice} inputMode="decimal" placeholder={String(price / 100)} onChange={(e) => setUnitPrice(e.target.value)} />
        </label>
        <label>
          Anledning
          <select className="w-full rounded border bg-card p-3" value={reason} onChange={(e) => setReason(e.target.value)}>
            {[
              ['overproduced', 'Överproducerat'],
              ['burnt', 'Bränt'],
              ['returned', 'Retur från gäst'],
              ['dropped', 'Tappat'],
              ['wrong_order', 'Fel beställning'],
              ['expired', 'För gammalt'],
              ['other', 'Annat'],
            ].map(([value, title]) => (
              <option key={value} value={value}>
                {title}
              </option>
            ))}
          </select>
        </label>
        <label>
          Anteckning
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <p>Totalt: {((Number(count.replace(',', '.')) * price) / 100 || 0).toFixed(2)} kr</p>
        {(error || query.error) && (
          <p role="alert" className="text-destructive">
            {error || graphqlErrorMessage(query.error)}
          </p>
        )}
        {query.data?.restaurant?.canWrite === false && <p>Kontot saknar behörighet att registrera svinn.</p>}
        <Button disabled={saving || !selected || query.data?.restaurant?.canWrite !== true} onClick={save}>
          Spara svinn
        </Button>
      </DialogContent>
    </Dialog>
  )
}
